import { useCallback, useEffect, useMemo, useState } from 'react';

import type { FieldProps, RJSFSchema } from '@rjsf/utils';
import { captureException } from '@sentry/react';
import { Bug, Expand } from 'lucide-react';

import { Button } from '@/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/shadcn/dialog';

import { normalizeTableErrors } from '@/components/forms/components/form/writer/fields/tableEditor/tableEditor.writer.utils';
import type { FormWriterContext } from '@/components/forms/components/form/writer/index';
import { FormDescription, FormLabel } from '@/components/forms/ui/ui.form';
import { isLockedWidget } from '@/components/forms/utils/links';
import { FormTableEditor } from '@/components/ui/dataEditor/form/tableEditor.form';
import type { TableEditorConfiguration } from '@/components/ui/dataEditor/form/tableEditor.form.type';
import type { TableRow } from '@/components/ui/table/table.type';
import { getByPath } from '@/utils';
import { executeStringFn } from '@/utils/executeFn';

type CustomFieldProps = FieldProps<TableRow[], RJSFSchema, FormWriterContext>;
type Props = CustomFieldProps & {
  uiSchema: Omit<CustomFieldProps['uiSchema'], 'ui:options'> & {
    'ui:options': {
      tableConfig: TableEditorConfiguration;
    };
  };
};

export const TableEditorFieldWriter = (props: Props) => {
  const config = props.uiSchema?.['ui:options']?.tableConfig;
  const errorSchema = props.errorSchema;

  const getInitialData = (): TableRow[] => {
    const { prefillFn, prefillPath } = config || {};

    if (props.formData && props.formData.length > 0) {
      return props.formData; // Use cached formdata in priority
    }
    if (prefillFn && props.formContext) {
      const fnData = executeStringFn<FormWriterContext, TableRow[]>(
        props.formContext,
        prefillFn
      );
      if (Array.isArray(fnData)) return fnData;
      if (fnData) {
        const message = 'The prefill function did not return an array.';
        console.error(message, fnData);
        captureException(message, {
          level: 'error',
          extra: {
            section: 'TableEditorFieldWriter',
            incorrectData: JSON.stringify(fnData, null, 2),
            prefillFn,
            formContext: JSON.stringify(props.formContext, null, 2)
          }
        });
      }
    }
    if (prefillPath && props.formContext?.traceContext?.state) {
      const pathData = getByPath(
        props.formContext.traceContext?.state,
        prefillPath
      );

      if (Array.isArray(pathData)) return pathData;
      if (pathData) {
        const message = `Found a the prefill path (${prefillPath}) but the data is not an array.`;
        console.error(message, pathData);
        captureException(message, {
          level: 'error',
          extra: {
            section: 'TableEditorFieldWriter',
            incorrectData: JSON.stringify(pathData, null, 2),
            state: JSON.stringify(
              props.formContext?.traceContext?.state,
              null,
              2
            ),
            prefillPath
          }
        });
      }
    }

    return [];
  };

  const [tableData, setTableData] = useState<TableRow[]>(() =>
    getInitialData()
  );

  const [selectedRows, setSelectedRows] = useState<string[]>(() => {
    const { selectionOutputKey, table } = config || {};
    if (!selectionOutputKey) return [];
    const selectorPath = table?.dataSelectorPath ?? 'rowId';
    return getInitialData()
      .filter(row => row[selectionOutputKey])
      .map(row => String(getByPath(row, selectorPath)));
  });

  const handleSelectedRowsChange = useCallback(
    (ids: string[]) => {
      setSelectedRows(ids);
      const { selectionOutputKey, table } = config || {};
      if (selectionOutputKey) {
        const selectorPath = table?.dataSelectorPath ?? 'rowId';
        const selectedSet = new Set(ids);
        setTableData(prev =>
          prev.map(row => ({
            ...row,
            [selectionOutputKey]: selectedSet.has(
              String(getByPath(row, selectorPath))
            )
          }))
        );
      }
    },
    [config, setTableData]
  );

  // Update the form data when tableData changes
  // TODO: try to directly use props.onChange in the table editor
  useEffect(() => {
    props.onChange(tableData);
  }, [tableData, props.onChange]);

  // Calculates errors only when dependencies change
  const normalizedErrors = useMemo(
    () => normalizeTableErrors(tableData, errorSchema, config),
    [tableData, errorSchema, config]
  );

  if (!config) {
    return (
      <section className="flex h-16 w-full items-center justify-center gap-4 rounded border border-destructive/20 bg-destructive/5 text-destructive">
        <Bug />
        <div>Table editor config is missing</div>
      </section>
    );
  }

  if (isLockedWidget(props.id, props.formContext?.links)) {
    // TODO: we could parse the table config and remove all editor properties.
    return (
      <section className="flex h-16 w-full items-center justify-center gap-4 rounded border border-destructive/20 bg-destructive/5 text-destructive">
        <Bug />
        <div>Locked state not yet implemented</div>
      </section>
    );
  }

  const { title, description } = props.schema;
  const id = props.idSchema.$id;

  return (
    <div className="h-130 w-full" id={id}>
      <section className="flex size-full flex-col">
        <header className="flex h-16 w-full items-end justify-between pb-2 text-sm">
          <section className="flex flex-col">
            <FormLabel
              formContext={props.formContext}
              id={id}
              label={title}
              required={props.required}
            />
            <FormDescription
              classNames="p-0"
              id={id}
              rawDescription={description}
            />
          </section>
          <section className="flex items-center gap-2">
            <Dialog>
              <DialogTrigger asChild>
                <Button
                  className="-mb-1"
                  size="icon"
                  type="button"
                  variant="ghost"
                >
                  <Expand />
                </Button>
              </DialogTrigger>
              <DialogContent className="h-[calc(100vh-2rem)] w-[calc(100vw-2rem)] max-w-none">
                <DialogHeader>
                  <DialogTitle>{title}</DialogTitle>
                  <DialogDescription>{description}</DialogDescription>
                </DialogHeader>

                <FormTableEditor
                  config={config}
                  description={description}
                  errorSchema={normalizedErrors}
                  onSelectedRowsChange={handleSelectedRowsChange}
                  onValueChange={setTableData}
                  required={props.required}
                  selectedRows={selectedRows}
                  title={title}
                  traceContext={props.formContext?.traceContext}
                  value={tableData}
                />
              </DialogContent>
            </Dialog>
          </section>
        </header>
        <FormTableEditor
          config={config}
          description={description}
          errorSchema={normalizedErrors}
          onSelectedRowsChange={handleSelectedRowsChange}
          onValueChange={setTableData}
          required={props.required}
          selectedRows={selectedRows}
          title={title}
          traceContext={props.formContext?.traceContext}
          value={tableData}
        />
      </section>
    </div>
  );
};
